/* eslint-disable fp/no-mutation */
import dayjs from 'dayjs'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import ComputerIcon from '@mui/icons-material/Computer'
import CloseIcon from '@mui/icons-material/Close'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'

import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import CreateIcon from '@mui/icons-material/Palette'
import ManagerIcon from '@mui/icons-material/AdminPanelSettings'
import SchoolIcon from '@mui/icons-material/School'

import theme from 'src/theme'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import withConfirm from 'src/components/common/with-confirm'
import { always, andThen, compose, pipe } from 'ramda'
import Subheader from '../../components/subheader'
import {
  fetchClasses,
  fetchLinkedServiceUsers,
  fetchLinkedServices,
  fetchUsers,
  linkedServiceUserRemoved,
  selectClassesStatus,
  selectLinkedServiceByName,
  selectLinkedServices,
  selectLinkedServicesByUserId,
  selectLinkedServicesStatus,
  selectProfileImageByUserID,
  selectUserByEmail,
  selectUserRolesByUserId,
  selectUsers,
  selectUsersStatus,
  resetUsers,
  selectRoles,
  selectRolesStatus,
  fetchRoles,
  fetchUserRoles,
  selectSubscriptionByUserID,
  selectUserAccessLevelByUserID,
  fetchUserSubscriptions,
  selectBoundTreeLimitedAccessLevel,
  selectBoundTreeFullAccessLevel,
  selectAccessLevelsStatus,
  fetchAccessLevels,
  fetchAllVideos,
  selectAllVideosStatus,
  fetchLiveClassSurveys,
  selectLiveClassSurveys,
  selectLiveClassSurveysStatus,
  fetchCourseSurveys,
  selectCourseSurveys,
  selectCourseSurveysStatus,
} from '../admin.slice'
import { CancelIcon, EditIcon, ScribeIcon, SubmitIcon } from '../../core/icons'
import Activity from './activity'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let [open, setOpen] = useState(false)
  let [formOpen, setFormOpen] = useState(false)
  let [formSaving, setFormSaving] = useState(false)
  let [subscriptionID, setSubscriptionID] = useState<string>('')
  let [subscriptionStatus, setSubscriptionStatus] = useState<string>('')
  let [searchValue, setSearchValue] = useState<string>('')
  let [selectedUser, setSelectedUser] = useState<string>('')
  let [selectedLinkedService, setSelectedLinkedService] = useState<string>('')
  let [liveClassesSearchOpen, setLiveClassesSearchOpen] = useState(false)
  let [coursesSearchOpen, setCoursesSearchOpen] = useState(false)
  let [selectedLiveClassSurveyURL, setSelectedLiveClassSurveyURL] =
    useState<string>('')
  let [selectedCourseSurveyURL, setSelectedCourseSurveyURL] =
    useState<string>('')

  let accessLevelsStatus = useAppSelector(selectAccessLevelsStatus)
  let boundtreeLimited = useAppSelector(selectBoundTreeLimitedAccessLevel)
  let boundtreeFull = useAppSelector(selectBoundTreeFullAccessLevel)
  let [modalStatus, setModalStatus] = useState<
    | 'None'
    | 'AddLinkedService'
    | 'DeleteLinkedService'
    | 'EditUserRole'
    | 'DeleteUser'
    | 'ReallyDeleteUser'
    | 'GrantBoundTreeFullAccess'
    | 'GrantBoundTreeLimitedAccess'
    | 'RemoveBoundTreeAccess'
  >('None')
  let [linkedServiceToRemove, setLinkedServiceToRemove] = useState<string>('')
  let [roleToChange, setRoleToChange] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [userFormStatus, setUserFormStatus] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)

  let usersStatus = useAppSelector(selectUsersStatus)
  let users = useAppSelector(selectUsers)
  let user = useAppSelector(selectUserByEmail(selectedUser))
  let profileImage = useAppSelector(selectProfileImageByUserID(user?.id ?? ''))
  let linkedServices = useAppSelector(
    selectLinkedServicesByUserId(user?.id ?? ''),
  )
  let userRoles = useAppSelector(selectUserRolesByUserId(user?.id ?? ''))
  let roles = useAppSelector(selectRoles)
  let rolesStatus = useAppSelector(selectRolesStatus)
  let liveClassSurveys = useAppSelector(selectLiveClassSurveys)
  let liveClassSurveysStatus = useAppSelector(selectLiveClassSurveysStatus)
  let courseSurveys = useAppSelector(selectCourseSurveys)
  let courseSurveysStatus = useAppSelector(selectCourseSurveysStatus)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let allLinkedServices = useAppSelector(selectLinkedServices)
  let classesStatus = useAppSelector(selectClassesStatus)
  let linkedServiceToAdd = useAppSelector(
    selectLinkedServiceByName(selectedLinkedService),
  )
  let userSubscription = useAppSelector(
    selectSubscriptionByUserID(user?.id ?? ''),
  )
  let userAccessLevel = useAppSelector(
    selectUserAccessLevelByUserID(user?.id ?? ''),
  )
  let userRoleAction = userRoles.some((ur) => ur.roleName === roleToChange)
    ? 'remove'
    : 'add'
  let videosStatus = useAppSelector(selectAllVideosStatus)

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (rolesStatus === 'uninitialized') {
      dispatch(fetchRoles())
    }
  }, [rolesStatus])

  useEffect(() => {
    if (accessLevelsStatus === 'uninitialized') {
      dispatch(fetchAccessLevels())
    }
  }, [accessLevelsStatus])

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchAllVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchLiveClassSurveys([user.id]))
    }
  }, [selectedUser])

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchCourseSurveys([user.id]))
    }
  }, [selectedUser])

  let closeSubscriptionForm = () => {
    setFormOpen(false)
    setSubscriptionID('')
    setSubscriptionStatus('')
  }

  let userForm = useFormik({
    validationSchema: yup.object({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      phone: yup.string().required(),
      username: yup.string().required(),
      email: yup.string().required(),
      address: yup.string().required(),
      city: yup.string().required(),
      state: yup.string().not(['unselected']).required(),
      zip: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      username: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/admin/user', {
          ...user,
          firstName: values.firstName,
          lastName: values.lastName,
          id: user?.id,
          phone: values.phone,
          email: values.username,
          primaryEmail: values.email,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => userForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let licenseForm = useFormik({
    validationSchema: yup.object({
      licenseNumber: yup.string(),
      licenseType: yup.string().required(),
      licenseState: yup.string().not(['unselected']).required(),
      licenseExpiration: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      licenseNumber: '',
      licenseType: '',
      licenseState: '',
      licenseExpiration: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/admin/user', {
          ...user,
          id: user?.id,
          licenseNumber: values.licenseNumber,
          licenseType: values.licenseType,
          licenseState: values.licenseState,
          licenseExpiration: values.licenseExpiration,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => licenseForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let nremtForm = useFormik({
    validationSchema: yup.object({
      nremtCertificationNumber: yup.string(),
      nremtCertificationDate: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      nremtCertificationNumber: '',
      nremtCertificationDate: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/admin/user', {
          ...user,
          id: user?.id,
          nremtCertificationNumber: values.nremtCertificationNumber,
          nremtCertificationDate: values.nremtCertificationDate,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => nremtForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let onUserEdit = () => {
    userForm.setFieldValue('firstName', user?.firstName)
    userForm.setFieldValue('lastName', user?.lastName)
    userForm.setFieldValue('phone', user?.phone)
    userForm.setFieldValue('username', user?.email)
    userForm.setFieldValue('email', user?.primaryEmail)
    userForm.setFieldValue('address', user?.address)
    userForm.setFieldValue('city', user?.city)
    userForm.setFieldValue('state', user?.state)
    userForm.setFieldValue('zip', user?.zip)
    setUserFormStatus('user')
  }

  let onLicenseEdit = () => {
    licenseForm.setFieldValue('licenseNumber', user?.licenseNumber)
    licenseForm.setFieldValue('licenseType', user?.licenseType)
    licenseForm.setFieldValue('licenseState', user?.licenseState)
    licenseForm.setFieldValue('licenseExpiration', user?.licenseExpiration)
    setUserFormStatus('license')
  }

  let onNremtEdit = () => {
    nremtForm.setFieldValue(
      'nremtCertificationNumber',
      user?.nremtCertificationNumber,
    )
    nremtForm.setFieldValue(
      'nremtCertificationDate',
      user?.nremtCertificationDate,
    )
    setUserFormStatus('nremt')
  }

  let addUserToLinkedService = () =>
    axios
      .post('/api/v2/admin/linked-service/user', {
        userID: user?.id,
        linkedServiceID: linkedServiceToAdd?.id,
      })
      .then(() => {
        dispatch(fetchLinkedServiceUsers([user?.id ?? '']))
        setSelectedLinkedService('')
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let removeUserFromLinkedService = () =>
    axios
      .delete(
        `/api/v2/admin/linked-service/user?userID=${user?.id}&linkedServiceID=${linkedServiceToRemove}`,
      )
      .then(() => {
        dispatch(
          linkedServiceUserRemoved([user?.id ?? '', linkedServiceToRemove]),
        )
        setLinkedServiceToRemove('')
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let changeUserRole = () =>
    axios
      .put('/api/v2/admin/user-roles', {
        userID: user?.id,
        roleID: roles.find((r) => r.name === roleToChange)?.id,
        action: userRoleAction,
      })
      .then(() => setModalStatus('None'))
      .then(() => setRoleToChange(''))
      .then(() => dispatch(fetchUserRoles([user?.id || ''])))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let deleteUser = () =>
    axios
      .delete(`/api/v2/admin/user?userID=${user?.id}`)
      .then(() => {
        setSelectedUser('')
        dispatch(resetUsers())
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let makeBoundTreeLimited = () =>
    axios
      .put('/api/v2/admin/access-level/user', {
        userID: user?.id,
        accessLevelID: boundtreeLimited?.id,
      })
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let makeBoundTreeFull = () =>
    axios
      .put('/api/v2/admin/access-level/user', {
        userID: user?.id,
        accessLevelID: boundtreeFull?.id,
      })
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let deleteBoundTreeAccess = () =>
    axios
      .delete(`/api/v2/admin/access-level/user?userID=${user?.id}`)
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let onUserSubscriptionUpdated = () => {
    if (user) {
      setFormSaving(true)
      axios
        .put('/api/v2/admin/subscription', {
          primaryEmail: user?.primaryEmail,
          subscriptionID,
          subscriptionStatus,
        })
        .catch((err) => {
          setFormSaving(false)
          setErrorMessage(
            err?.response?.data ||
              err?.message ||
              'Error Updating Subscription Info',
          )
        })
        .then(() => dispatch(fetchUserSubscriptions([user!.id])))
        .then(() => setSubscriptionID(''))
        .then(() => setSubscriptionStatus(''))
        .then(() => setFormSaving(false))
        .then(() => closeSubscriptionForm())
    }
  }

  let ConfirmAddToLinkedService = withConfirm(
    'Add User to Linked Service',
    `Are you sure you want to add ${user?.firstName} ${user?.lastName} to ${linkedServiceToAdd?.name}?`,
    modalStatus === 'AddLinkedService',
    compose(setModalStatus, always('None') as any),
    pipe(
      addUserToLinkedService,
      andThen(compose(setModalStatus, always('None') as any)),
    ),
  )

  let ConfirmRemoveFromLinkedService = withConfirm(
    'Remove User From Linked Service',
    `Are you sure you want to remove ${user?.firstName} ${user?.lastName} from this service?`,
    modalStatus === 'DeleteLinkedService',
    compose(setModalStatus, always('None') as any),
    pipe(
      removeUserFromLinkedService,
      andThen(compose(setModalStatus, always('None') as any)),
    ),
  )

  let ConfirmChangeUserRole = withConfirm(
    'Edit User Role',
    roleToChange === 'admin' && userRoleAction === 'add'
      ? `The admin role is reserved for FOAMfrat employees only. Giving a user admin access results in an audit log in our database so that we can keep track of who has had access. Are you sure you want to add the admin role for ${user?.firstName} ${user?.lastName}?`
      : `Are you sure you want to ${userRoleAction} the ${roleToChange} role for ${user?.firstName} ${user?.lastName}?`,
    modalStatus === 'EditUserRole',
    compose(setModalStatus, always('None') as any),
    pipe(
      changeUserRole,
      andThen(compose(setModalStatus, always('None') as any)),
    ),
  )

  let ConfirmDeleteUser = withConfirm(
    'Delete User?',
    `WARNING! Deleting a user is an irreversible action. It is NOT our practice to delete innactive or cancelled users. It is important that innactive accounts are not deleted so that historical data can still be accessed for our own purposes, as well as for auditing purposes such as for CAPCE and other accreditation agencies.
    
    The only acceptable case for deleting a user's account is if they insist that their personal information be removed as per our privacy policy.
    
    Are you sure you want to delete ${user?.firstName} ${user?.lastName}?`,
    modalStatus === 'DeleteUser',
    compose(setModalStatus, always('None') as any),
    compose(setModalStatus, always('ReallyDeleteUser') as any),
  )

  let ReallyConfirmDeleteUser = withConfirm(
    'Are you absolutely sure?',
    'The devs cannot save you.',
    modalStatus === 'ReallyDeleteUser',
    compose(setModalStatus, always('None') as any),
    pipe(deleteUser, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmBoundTreeLimited = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to grant this user Bound Tree limited access?',
    modalStatus === 'GrantBoundTreeLimitedAccess',
    compose(setModalStatus, always('None') as any),
    pipe(makeBoundTreeLimited, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmBoundTreeFull = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to grant this user Bound Tree full access?',
    modalStatus === 'GrantBoundTreeFullAccess',
    compose(setModalStatus, always('None') as any),
    pipe(makeBoundTreeFull, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmRemoveBoundtree = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to remove this user from Bound Tree?',
    modalStatus === 'RemoveBoundTreeAccess',
    compose(setModalStatus, always('None') as any),
    pipe(deleteBoundTreeAccess, compose(setModalStatus, always('None') as any)),
  )

  return (
    <>
      <div className="flex items-center justify-center mt-2 mb-4">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <PersonIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          User Administration
        </Typography>
      </div>
      <Container maxWidth="xl">
        <Autocomplete
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName} | ${option.primaryEmail} | ${option.email}`
          }
          onInputChange={(_, value) => setSelectedUser(value.split(' | ')[1])}
          options={users}
          loading={usersStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  dispatch(fetchUsers(searchValue))
                }
              }}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Username or Email"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {
                        <SearchIcon
                          sx={{ cursor: 'pointer' }}
                          onClick={() => dispatch(fetchUsers(searchValue))}
                        />
                      }
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {user && (
          <>
            <Subheader title="User Profile" />
            <Paper
              variant="outlined"
              className="w-full"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'center',
                p: 3,
              }}
            >
              <Box sx={{ textAlign: 'center', marginX: 'auto' }}>
                {profileImage ? (
                  <Avatar
                    sx={{ width: 150, height: 150, marginX: 'auto' }}
                    src={profileImage.imageName}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 150,
                      height: 150,
                      marginX: 'auto',
                      fontSize: 64,
                    }}
                  >
                    {`${user?.firstName[0]}${user?.lastName[0]}`.toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {`${user?.firstName} ${user?.lastName}`}
                  <IconButton sx={{ ml: 1 }} size="small" onClick={onUserEdit}>
                    <EditIcon />
                  </IconButton>
                </Typography>
                <div>{user?.phone}</div>
                <div>{user?.email}</div>
                <div>{user?.primaryEmail}</div>
                <div>{user?.address}</div>
                {user && user.city && user.state && user.zip && (
                  <div>
                    {user?.city}, {user?.state} {user?.zip}
                  </div>
                )}
                {userAccessLevel ? (
                  <Chip
                    label={`BT ${userAccessLevel.accessLevel}`}
                    onClick={() => {
                      setModalStatus(
                        userAccessLevel?.accessLevel.toLowerCase() === 'limited'
                          ? 'GrantBoundTreeFullAccess'
                          : 'GrantBoundTreeLimitedAccess',
                      )
                    }}
                    variant="filled"
                    onDelete={() => {
                      setModalStatus('RemoveBoundTreeAccess')
                    }}
                    sx={{
                      mt: 1,
                      color: 'black',
                      backgroundColor: '#ffd650',
                    }}
                  />
                ) : (
                  <Chip
                    label={`No Access Level`}
                    variant="outlined"
                    onClick={() => {
                      setModalStatus('GrantBoundTreeLimitedAccess')
                    }}
                    sx={{
                      mt: 1,
                      color: '#ffd650',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ marginX: 'auto' }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Roles
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                    width: '100%',
                    marginTop: 3,
                    marginBottom: 12,
                  }}
                >
                  {[
                    { name: 'Admin', icon: <ComputerIcon /> },
                    { name: 'Director', icon: <CorporateFareIcon /> },
                    { name: 'Manager', icon: <ManagerIcon /> },
                    { name: 'Creator', icon: <CreateIcon /> },
                    { name: 'Student', icon: <SchoolIcon /> },
                    { name: 'Scribe', icon: <ScribeIcon /> },
                  ].map((role, i) => {
                    let hasRole = userRoles.some(
                      (ur) => ur.roleName === role.name.toLocaleLowerCase(),
                    )
                    let isStatic = role.name === 'Student' && hasRole

                    return (
                      <Chip
                        key={i}
                        label={role.name}
                        color="primary"
                        variant={hasRole ? 'filled' : 'outlined'}
                        icon={role.icon}
                        clickable={!isStatic}
                        onClick={() => {
                          if (!isStatic) {
                            setRoleToChange(role.name.toLocaleLowerCase())
                            setModalStatus('EditUserRole')
                          }
                        }}
                      />
                    )
                  })}
                </div>
                <Divider sx={{ marginY: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    License
                  </Typography>
                  <IconButton size="small" onClick={onLicenseEdit}>
                    <EditIcon />
                  </IconButton>
                </Box>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}
                >
                  <div>
                    <strong>Number:</strong> {user?.licenseNumber || 'Unknown'}
                  </div>
                  <div>
                    <strong>Type:</strong> {user?.licenseType || 'Unknown'}
                  </div>
                  <div>
                    <strong>State:</strong> {user?.licenseState || 'Unknown'}
                  </div>
                  <div>
                    <strong>Expiration:</strong>{' '}
                    {user?.licenseExpiration
                      ? dayjs(user?.licenseExpiration).format('MM/DD/YYYY')
                      : 'Unknown'}
                  </div>
                </div>
                <Divider sx={{ marginY: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    NREMT Certification
                  </Typography>
                  <IconButton size="small" onClick={onNremtEdit}>
                    <EditIcon />
                  </IconButton>
                </Box>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}
                >
                  <div>
                    <strong>Number:</strong>{' '}
                    {user?.nremtCertificationNumber || 'Unknown'}
                  </div>
                  <div>
                    <strong>Date:</strong>{' '}
                    {user?.nremtCertificationDate
                      ? dayjs(user?.nremtCertificationDate).format('MM/DD/YYYY')
                      : 'Unknown'}
                  </div>
                </div>
                <Divider sx={{ marginY: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Subscription Information
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={!userSubscription}
                    onClick={() => {
                      setSubscriptionID(userSubscription?.subscriptionID || '')
                      setSubscriptionStatus(
                        userSubscription?.subscriptionStatus || '',
                      )
                      setFormOpen(true)
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}
                >
                  <div>
                    <strong>ID:</strong>{' '}
                    {userSubscription?.subscriptionID || 'Unknown'}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    {userSubscription?.subscriptionStatus
                      ? `${userSubscription.subscriptionStatus[0].toUpperCase()}${userSubscription.subscriptionStatus.slice(1)}`
                      : 'Unknown'}
                  </div>
                  <div>
                    <strong>Expiration:</strong>{' '}
                    {userSubscription?.subscriptionEndDate
                      ? dayjs(userSubscription?.subscriptionEndDate).format(
                          'MM/DD/YYYY',
                        )
                      : 'Unknown'}
                  </div>
                  <div>
                    <strong>Customer ID:</strong>{' '}
                    {userSubscription?.customerID || 'Unknown'}
                  </div>
                </div>
                <Dialog open={formOpen} onClose={closeSubscriptionForm}>
                  <Paper sx={{ width: 300, px: 2, py: 1 }}>
                    <DialogTitle
                      sx={{ textAlign: 'center', marginBottom: -1.5 }}
                    >
                      Update Subscription Info
                    </DialogTitle>
                    <DialogContent
                      sx={{
                        px: 2,
                        pb: 2,
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'column',
                      }}
                    >
                      <TextField
                        sx={{ mt: 1 }}
                        variant="outlined"
                        size="small"
                        label="Subscription ID"
                        type="text"
                        fullWidth
                        value={subscriptionID}
                        onChange={(e: React.ChangeEvent) =>
                          setSubscriptionID(
                            (e.target as HTMLInputElement).value,
                          )
                        }
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Subscription Status</InputLabel>
                        <Select
                          fullWidth
                          label="Subscription Status"
                          value={subscriptionStatus}
                          onChange={(e) =>
                            setSubscriptionStatus(e.target.value)
                          }
                        >
                          {userSubscription?.subscriptionStatus ===
                            'pending' && (
                            <MenuItem key="pending" value="pending">
                              Pending
                            </MenuItem>
                          )}
                          {userSubscription?.subscriptionStatus === 'trial' && (
                            <MenuItem key="trial" value="trial">
                              Trial
                            </MenuItem>
                          )}
                          {['Active', 'Cancelled'].map((s) => (
                            <MenuItem key={s} value={s.toLowerCase()}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </DialogContent>
                    <DialogActions
                      sx={{ display: 'flex', justifyContent: 'center', pb: 3 }}
                    >
                      {formSaving ? (
                        <CircularProgress size={36} />
                      ) : (
                        <>
                          <Button
                            onClick={onUserSubscriptionUpdated}
                            color="primary"
                            variant="contained"
                            sx={{ flex: '1 1 0px' }}
                            startIcon={<SubmitIcon />}
                            disabled={
                              !subscriptionID ||
                              !(
                                subscriptionStatus === 'active' ||
                                subscriptionStatus === 'cancelled'
                              )
                            }
                          >
                            Submit
                          </Button>
                          <Button
                            onClick={closeSubscriptionForm}
                            color="error"
                            variant="contained"
                            sx={{ flex: '1 1 0px' }}
                            startIcon={<CancelIcon />}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </DialogActions>
                  </Paper>
                </Dialog>
              </Box>
            </Paper>
            <Box flexGrow={1}>
              <Subheader title="Linked Services" />
              <Grid container spacing={2}>
                <Grid xs={12} md={6} lg={4}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        align="center"
                        gutterBottom
                      >
                        Add To Linked Service
                      </Typography>
                      <Autocomplete
                        className="mx-auto my-4"
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionLabel={(option) => `${option.name}`}
                        onInputChange={(_, value) =>
                          setSelectedLinkedService(value)
                        }
                        disablePortal
                        options={allLinkedServices}
                        sx={{ width: 300 }}
                        renderInput={(params) => (
                          <TextField {...params} label="Linked Service" />
                        )}
                      />
                    </CardContent>
                    {!!linkedServiceToAdd && (
                      <>
                        <CardContent
                          sx={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundImage: `url(${linkedServiceToAdd.imageName})`,
                            backgroundSize: 'cover',
                            border: 0,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                          }}
                        />
                        <CardContent>{linkedServiceToAdd?.name}</CardContent>
                      </>
                    )}
                    <CardActions>
                      <Button
                        size="small"
                        disabled={!linkedServiceToAdd?.name}
                        color="primary"
                        onClick={
                          compose(
                            setModalStatus,
                            always('AddLinkedService') as any,
                          ) as any
                        }
                      >
                        Add
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
                <Grid xs={12} md={6} lg={8}>
                  <Grid container spacing={2}>
                    {linkedServices.map((service) => (
                      <Grid key={service.id}>
                        <Chip
                          avatar={
                            <Avatar src={service.imageName}>
                              {service.name.toUpperCase()}
                            </Avatar>
                          }
                          label={service.name}
                          onDelete={pipe(
                            compose(
                              setLinkedServiceToRemove,
                              always(service.id) as any,
                            ) as any,
                            compose(
                              setModalStatus,
                              always('DeleteLinkedService') as any,
                            ) as any,
                          )}
                          color="primary"
                          deleteIcon={<CloseIcon />}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box flexGrow={1}>
              <Subheader title="Surveys" />
              <div className="flex flex-col justify-center mt-2">
                <Autocomplete
                  className="my-4"
                  open={coursesSearchOpen}
                  onOpen={() => {
                    setCoursesSearchOpen(true)
                  }}
                  onClose={() => {
                    setCoursesSearchOpen(false)
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.courseName === value.courseName
                  }
                  getOptionLabel={(option) =>
                    `${option.courseName} | Completed ${dayjs(
                      option.quizCompletedTime,
                    ).format('MM/DD/YYYY')}`
                  }
                  onInputChange={(_, value) =>
                    setSelectedCourseSurveyURL(
                      courseSurveys.find(
                        (lcs) =>
                          lcs.courseName === value.split(' | ')[0] &&
                          lcs.passOrFail === 'pass',
                      )?.surveyUrl || '',
                    )
                  }
                  options={courseSurveys.filter(
                    (lcs) => lcs.passOrFail === 'pass',
                  )}
                  loading={courseSurveysStatus === 'pending'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Courses"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {<SearchIcon sx={{ cursor: 'pointer' }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={!selectedCourseSurveyURL}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(
                        `https://ce.foamfrat.com${selectedCourseSurveyURL}`,
                      )
                      .then(() => {
                        setSuccessMessage(
                          'Copied course survey link to clipboard!',
                        )
                      })
                  }}
                >
                  Generate Link
                </Button>
              </Box>
              <Divider />
              <div className="flex flex-col justify-center mt-2">
                <Autocomplete
                  className="my-4"
                  open={liveClassesSearchOpen}
                  onOpen={() => {
                    setLiveClassesSearchOpen(true)
                  }}
                  onClose={() => {
                    setLiveClassesSearchOpen(false)
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.className === value.className
                  }
                  getOptionLabel={(option) =>
                    `${option.className} | Completed ${dayjs(
                      option.quizCompletedTime,
                    ).format('MM/DD/YYYY')}`
                  }
                  onInputChange={(_, value) =>
                    setSelectedLiveClassSurveyURL(
                      liveClassSurveys.find(
                        (lcs) =>
                          lcs.className === value.split(' | ')[0] &&
                          lcs.passOrFail === 'pass',
                      )?.surveyUrl || '',
                    )
                  }
                  options={liveClassSurveys.filter(
                    (lcs) => lcs.passOrFail === 'pass',
                  )}
                  loading={liveClassSurveysStatus === 'pending'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Live Classes"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {<SearchIcon sx={{ cursor: 'pointer' }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={!selectedLiveClassSurveyURL}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(
                        `https://ce.foamfrat.com${selectedLiveClassSurveyURL}`,
                      )
                      .then(() => {
                        setSuccessMessage(
                          'Copied live class survey link to clipboard!',
                        )
                      })
                  }}
                >
                  Generate Link
                </Button>
              </Box>
            </Box>
            <Box flexGrow={1}>
              <Subheader title="Activity" />
              <Activity selectedUserEmail={selectedUser} />
            </Box>
            <Box flexGrow={1}>
              <Subheader title="Danger Zone" />
              <Button
                color="error"
                variant="contained"
                onClick={
                  compose(setModalStatus, always('DeleteUser') as any) as any
                }
              >
                Delete User
              </Button>
            </Box>
          </>
        )}
      </Container>
      <ConfirmAddToLinkedService />
      <ConfirmRemoveFromLinkedService />
      <ConfirmChangeUserRole />
      <ConfirmDeleteUser />
      <ReallyConfirmDeleteUser />
      <ConfirmBoundTreeLimited />
      <ConfirmBoundTreeFull />
      <ConfirmRemoveBoundtree />
      <Dialog
        open={userFormStatus === 'user'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={userForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit User Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.firstName}
                label="First Name"
                name="firstName"
                onChange={userForm.handleChange}
                error={
                  userForm.touched.firstName && !!userForm.errors.firstName
                }
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.lastName}
                label="Last Name"
                name="lastName"
                onChange={userForm.handleChange}
                error={userForm.touched.lastName && !!userForm.errors.lastName}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.phone}
                label="Phone"
                name="phone"
                onChange={userForm.handleChange}
                error={userForm.touched.phone && !!userForm.errors.phone}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.username}
                label="Username"
                name="username"
                onChange={userForm.handleChange}
                error={userForm.touched.username && !!userForm.errors.username}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.email}
                label="Email"
                name="email"
                onChange={userForm.handleChange}
                error={userForm.touched.email && !!userForm.errors.email}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.address}
                label="Address"
                name="address"
                onChange={userForm.handleChange}
                error={userForm.touched.address && !!userForm.errors.address}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.city}
                label="City"
                name="city"
                onChange={userForm.handleChange}
                error={userForm.touched.city && !!userForm.errors.city}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>State</InputLabel>
                <Select
                  variant="standard"
                  value={userForm.values.state}
                  label="State"
                  name="state"
                  onChange={userForm.handleChange}
                  error={userForm.touched.state && !!userForm.errors.state}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Antigua and Barbuda">
                    Antigua and Barbuda
                  </MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="The Bahamas">The Bahamas</MenuItem>
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="DHS">DHS</MenuItem>
                  <MenuItem value="District of Columbia">
                    District of Columbia
                  </MenuItem>
                  <MenuItem value="Ecuador">Ecuador</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Great Britain">Great Britain</MenuItem>
                  <MenuItem value="Guam">Guam</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iraq">Iraq</MenuItem>
                  <MenuItem value="Ireland">Ireland</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="New Zealand">New Zealand</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
                  <MenuItem value="Poland">Poland</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                  <MenuItem value="South Africa">South Africa</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="Scotland">Scotland</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Slovakia">Slovakia</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Trinidad and Tobago">
                    Trinidad and Tobago
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.zip}
                label="ZIP"
                name="zip"
                onChange={userForm.handleChange}
                error={userForm.touched.zip && !!userForm.errors.zip}
              />
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog
        open={userFormStatus === 'license'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={licenseForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit License Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={licenseForm.values.licenseNumber}
                label="License Number"
                name="licenseNumber"
                onChange={licenseForm.handleChange}
                error={
                  licenseForm.touched.licenseNumber &&
                  !!licenseForm.errors.licenseNumber
                }
              />
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>License Type</InputLabel>
                <Select
                  variant="standard"
                  value={licenseForm.values.licenseType}
                  label="License Type"
                  name="licenseType"
                  onChange={licenseForm.handleChange}
                  error={
                    licenseForm.touched.licenseType &&
                    !!licenseForm.errors.licenseType
                  }
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="" />
                  <MenuItem value="Paramedic">Paramedic</MenuItem>
                  <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
                  <MenuItem value="EMR">Emergency Medical Responder</MenuItem>
                  <MenuItem value="CFR">Certified First Responder</MenuItem>
                  <MenuItem value="EMT">EMT</MenuItem>
                  <MenuItem value="EMT-1">EMT-1</MenuItem>
                  <MenuItem value="EMT-B">EMT-B</MenuItem>
                  <MenuItem value="EMT-D">EMT-D</MenuItem>
                  <MenuItem value="AEMT">AEMT</MenuItem>
                  <MenuItem value="EMT-2">EMT-2</MenuItem>
                  <MenuItem value="EMT-CC">EMT-CC</MenuItem>
                  <MenuItem value="Advanced Care Paramedic (Canada Only)">
                    Advanced Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Primary Care Paramedic (Canada Only)">
                    Primary Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Physician">Physician</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>License State</InputLabel>
                <Select
                  variant="standard"
                  value={licenseForm.values.licenseState}
                  label="License State"
                  name="licenseState"
                  onChange={licenseForm.handleChange}
                  error={
                    licenseForm.touched.licenseState &&
                    !!licenseForm.errors.licenseState
                  }
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="Alaska">Alaska</MenuItem>
                  <MenuItem value="Alabama">Alabama</MenuItem>
                  <MenuItem value="Antigua and Barbuda">
                    Antigua and Barbuda
                  </MenuItem>
                  <MenuItem value="Arkansas">Arkansas</MenuItem>
                  <MenuItem value="Australia">Australia</MenuItem>
                  <MenuItem value="Arizona">Arizona</MenuItem>
                  <MenuItem value="The Bahamas">The Bahamas</MenuItem>
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="California">California</MenuItem>
                  <MenuItem value="Canada">Canada</MenuItem>
                  <MenuItem value="Colorado">Colorado</MenuItem>
                  <MenuItem value="Connecticut">Connecticut</MenuItem>
                  <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                  <MenuItem value="Delaware">Delaware</MenuItem>
                  <MenuItem value="DHS">DHS</MenuItem>
                  <MenuItem value="District of Columbia">
                    District of Columbia
                  </MenuItem>
                  <MenuItem value="Ecuador">Ecuador</MenuItem>
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Georgia">Georgia</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Great Britain">Great Britain</MenuItem>
                  <MenuItem value="Guam">Guam</MenuItem>
                  <MenuItem value="Hawaii">Hawaii</MenuItem>
                  <MenuItem value="Iowa">Iowa</MenuItem>
                  <MenuItem value="Idaho">Idaho</MenuItem>
                  <MenuItem value="Illinois">Illinois</MenuItem>
                  <MenuItem value="Indiana">Indiana</MenuItem>
                  <MenuItem value="Iraq">Iraq</MenuItem>
                  <MenuItem value="Ireland">Ireland</MenuItem>
                  <MenuItem value="Kansas">Kansas</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Louisiana">Louisiana</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                  <MenuItem value="Massachusetts">Massachusetts</MenuItem>
                  <MenuItem value="Maryland">Maryland</MenuItem>
                  <MenuItem value="Maine">Maine</MenuItem>
                  <MenuItem value="Mexico">Mexico</MenuItem>
                  <MenuItem value="Michigan">Michigan</MenuItem>
                  <MenuItem value="Minnesota">Minnesota</MenuItem>
                  <MenuItem value="Missouri">Missouri</MenuItem>
                  <MenuItem value="Mississippi">Mississippi</MenuItem>
                  <MenuItem value="Montana">Montana</MenuItem>
                  <MenuItem value="North Carolina">North Carolina</MenuItem>
                  <MenuItem value="North Dakota">North Dakota</MenuItem>
                  <MenuItem value="Nebraska">Nebraska</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="New Zealand">New Zealand</MenuItem>
                  <MenuItem value="New Hampshire">New Hampshire</MenuItem>
                  <MenuItem value="New Jersey">New Jersey</MenuItem>
                  <MenuItem value="New Mexico">New Mexico</MenuItem>
                  <MenuItem value="Nevada">Nevada</MenuItem>
                  <MenuItem value="New York">New York</MenuItem>
                  <MenuItem value="Ohio">Ohio</MenuItem>
                  <MenuItem value="Oklahoma">Oklahoma</MenuItem>
                  <MenuItem value="Oregon">Oregon</MenuItem>
                  <MenuItem value="Pennsylvania">Pennsylvania</MenuItem>
                  <MenuItem value="Puerto Rico">Puerto Rico</MenuItem>
                  <MenuItem value="Poland">Poland</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Rhode Island">Rhode Island</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Serbia">Serbia</MenuItem>
                  <MenuItem value="South Africa">South Africa</MenuItem>
                  <MenuItem value="South Carolina">South Carolina</MenuItem>
                  <MenuItem value="Scotland">Scotland</MenuItem>
                  <MenuItem value="South Dakota">South Dakota</MenuItem>
                  <MenuItem value="Slovakia">Slovakia</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                  <MenuItem value="Texas">Texas</MenuItem>
                  <MenuItem value="Trinidad and Tobago">
                    Trinidad and Tobago
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Utah">Utah</MenuItem>
                  <MenuItem value="Virgin Islands">Virgin Islands</MenuItem>
                  <MenuItem value="Virginia">Virginia</MenuItem>
                  <MenuItem value="Vermont">Vermont</MenuItem>
                  <MenuItem value="Washington">Washington</MenuItem>
                  <MenuItem value="Wisconsin">Wisconsin</MenuItem>
                  <MenuItem value="West Virginia">West Virginia</MenuItem>
                  <MenuItem value="Wyoming">Wyoming</MenuItem>
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="License Expiration Date"
                  value={dayjs(licenseForm.values.licenseExpiration)}
                  onChange={(dayJsVal: any) =>
                    licenseForm.handleChange({
                      target: {
                        name: 'licenseExpiration',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog
        open={userFormStatus === 'nremt'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={nremtForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit NREMT Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={nremtForm.values.nremtCertificationNumber}
                label="NREMT Certification Number"
                name="nremtCertificationNumber"
                onChange={nremtForm.handleChange}
                error={
                  nremtForm.touched.nremtCertificationNumber &&
                  !!nremtForm.errors.nremtCertificationNumber
                }
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="NREMT Expiration Date"
                  value={dayjs(nremtForm?.values?.nremtCertificationDate)}
                  onChange={(dayJsVal: any) =>
                    nremtForm.handleChange({
                      target: {
                        name: 'nremtCertificationDate',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setSuccessMessage('')}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
